import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useStyles } from '~/assets/styles';
import { ClaimContextProvider } from '~/components/ClaimContainer';
import mixpanel from '~/components/CmsMain/mixpanel';
import SendEmailCommunicationCardContainer from '~/components/communications/EmailCommunicationCard/SendEmailCommunicationCardContainer';
import CommunicationPopoverLoader from '~/components/Contacts/ContactCommunications/CommunicationPopoverLoader';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import { useMinimizedDialogs } from '~/components/core/MinimizedBar/Context';
import { useCms } from '~/components/hooks/useCms';
import EmailIcon from '~/components/icons/notifications/EmailIcon';
import InlineIconButton from '~/components/InlineIconButton';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

const SendEmailCommunicationCardContainerWithClaimContext = ({ claimId, ...props }) => {
  const [claim, isLoadingClaim, isError, onClaimUpdate] = useFetchClaim(claimId);

  return (
    <LoadingSwitch
      isLoading={isLoadingClaim}
      isError={isError}
      LoadingComponent={CommunicationPopoverLoader}
      createMinimizedContext={false}
    >
      <ClaimContextProvider refreshData={onClaimUpdate} claim={claim}>
        <SendEmailCommunicationCardContainer {...props} />
      </ClaimContextProvider>
    </LoadingSwitch>
  );
};

SendEmailCommunicationCardContainerWithClaimContext.propTypes = {
  claimId: PropTypes.number,
};

// Whatever stops this popover's trigger from opening the composer, for a caller that opens it itself.
export const isEmailCommunicationDisabled = (user, contact) =>
  contact.is_emailing_allowed === false || user.role.is_view_only;

// Opened from its own trigger button, unless the caller passes `isOpen`: then no trigger renders, the
// composer is launched when `isOpen` turns true, and `onClose` is called once it is closed or sent.
const EmailCommunicationPopover = ({
  contact,
  onUpdate,
  communicationDetails = {},
  mixpanelSource,
  ignorePermissions = false,
  emailTitle,
  emailText,
  useIconButton = false,
  ariaLabel,
  claimId,
  isOpen,
  onClose,
}) => {
  const { classes } = useStyles();
  const { user } = useCms();
  const { add } = useMinimizedDialogs();
  const isControlled = isOpen !== undefined;

  const handleOpenDialog = () => {
    mixpanel.track(MIXPANEL_EVENTS.NEW_EMAIL_COMMUNICATION_CLICKED, { source: mixpanelSource });

    const type = 'EMAIL';
    const { handleCloseDialog } = add({
      barHeader: `New Email: ${emailTitle || contact.full_name}`,
      type,
      dialogComponent: SendEmailCommunicationCardContainerWithClaimContext,
      dialogProps: {
        contact,
        contactEmailId: communicationDetails.id,
        emailTitle,
        emailText,
        claimId,
        shouldDisplaySupervisorSenderSelection: true,
        onMinimized: () => {
          mixpanel.track(MIXPANEL_EVENTS.MINIMIZED_DIALOG, {
            source: mixpanelSource,
            type,
          });
        },
        onClose: () => {
          handleCloseDialog();
          onClose?.();
        },
        onSendEmail: () => {
          handleCloseDialog();
          if (onUpdate) {
            onUpdate();
          }
          onClose?.();
        },
      },
    });
  };

  // The registry renders the composer, so opening it is a one-off launch rather than a render: launch once
  // each time `isOpen` turns true. One ref holds the latest launcher so the effect runs only on that change;
  // the other keeps a re-run effect (StrictMode's remount) from launching a second composer.
  const openDialogRef = useRef(handleOpenDialog);
  openDialogRef.current = handleOpenDialog;
  const hasLaunchedRef = useRef(false);
  useEffect(() => {
    if (!isControlled || !isOpen) {
      hasLaunchedRef.current = false;
      return;
    }
    if (!hasLaunchedRef.current) {
      hasLaunchedRef.current = true;
      openDialogRef.current();
    }
  }, [isControlled, isOpen]);

  const isDisabled = isEmailCommunicationDisabled(user, contact);

  return (
    <>
      {isControlled ? null : (
        <InlineIconButton
          icon={EmailIcon}
          onClick={handleOpenDialog}
          disabled={isDisabled}
          className={cn({ [classes.hoverableIcon]: !isDisabled })}
          ignorePermissions={ignorePermissions}
          useIconButton={useIconButton}
          ariaLabel={ariaLabel}
        />
      )}
    </>
  );
};

EmailCommunicationPopover.propTypes = {
  contact: PropTypes.object.isRequired,
  onUpdate: PropTypes.func,
  communicationDetails: PropTypes.object,
  mixpanelSource: PropTypes.string,
  ignorePermissions: PropTypes.bool,
  useIconButton: PropTypes.bool,
  ariaLabel: PropTypes.string,
  emailTitle: PropTypes.string,
  emailText: PropTypes.string,
  claimId: PropTypes.number,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default EmailCommunicationPopover;
